import type { ComparisonDeckStats, PlayerProfile } from '@sleeved/lib';
import {
  palette,
  glass,
  glassBorder,
  glassText,
  fonts,
} from '../../styles/overlayTokens';
import type { AnimPhase } from './chartPrimitives';
import {
  p1rgba,
  p2rgba,
  makeAnimCls,
  SectionDivider,
  ColorPips,
  DonutTypeChart,
  MiniHeader,
  MkDistributionBar,
  MiniBarChart,
  KeywordBubbleCloud,
  MiniHorizontalBars,
  MiniHeatmap,
  BinaryDistributionBar,
  FeaturedCardAvatarCircle,
  DensePanelPlaceholder,
} from './chartPrimitives';

// ---------------------------------------------------------------------------
// gundam — GundamDensePanel + GundamRailPanel. Split out of the former
// DeckOverlayPanels.tsx monolith (pure move, no behavior change).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// GundamDensePanel — dense half-width compare panel for Gundam Card Game.
// Sections: identity (single circle) → colors → card types → level dist →
//           cost dist → trait breakdown → keywords (cap 12).
// ---------------------------------------------------------------------------

export interface GundamDensePanelProps {
  side: 'p1' | 'p2';
  stats: ComparisonDeckStats | null;
  peerHasStats: boolean;
  playerName: string;
  archetype: string;
  phase: AnimPhase;
  reducedMotion: boolean;
  featuredCardImageUrl?: PlayerProfile['featuredCardImageUrl'];
  levelSharedMax: number;
  levelLabelUnion: string[];
  costSharedMax: number;
  costLabelUnion: string[];
  fullWidth?: boolean;
  naturalLayout?: boolean;
  placeholderText?: string;
}

export function GundamDensePanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  featuredCardImageUrl,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelSharedMax: _levelSharedMax,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelLabelUnion: _levelLabelUnion,
  costSharedMax,
  costLabelUnion,
  fullWidth,
  naturalLayout,
  placeholderText,
}: GundamDensePanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  const isP1Layout = isP1 || !!naturalLayout;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const traitDist = stats?.subtypeDistributions?.[0] ?? null;
  const top5Traits = traitDist?.data
    ? [...traitDist.data].sort((a, b) => b.count - a.count).slice(0, 5)
    : [];
  const traitMax = top5Traits.length > 0 ? Math.max(...top5Traits.map(e => e.count)) : 1;
  const traitHeader = traitDist ? traitDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'TRAIT BREAKDOWN';

  const top12Keywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count).slice(0, 12)
    : [];
  const keywordOverflow = (stats?.keywordDistribution?.length ?? 0) - top12Keywords.length;

  const levelHeader = stats?.costCurveLabel ? stats.costCurveLabel.toUpperCase() : 'LEVEL DISTRIBUTION';
  const costHeader = stats?.secondaryCurveLabel ? stats.secondaryCurveLabel.toUpperCase() : 'COST DISTRIBUTION';

  return (
    <div
      aria-hidden="true"
      className={animCls(isP1Layout ? 'dcao-p1-enter' : 'dcao-p2-enter')}
      style={{
        flex: fullWidth ? 1 : '0 0 680px',
        maxWidth: fullWidth ? 1400 : undefined,
        margin: fullWidth ? '0 auto' : undefined,
        height: '100%',
        position: 'relative',
        background: glass.card,
        borderRadius: 16,
        border: `1px solid ${glassBorder.light}`,
        overflow: 'hidden',
        ...(isP1Layout
          ? { borderRight: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset -4px 0 20px ${accentRgba(0.12)}` }
          : { borderLeft: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset 4px 0 20px ${accentRgba(0.12)}` }),
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, borderRadius: '16px 16px 0 0', background: accentColor }} />
      <div
        id={`dcao-content-${side}`}
        style={{
          position: 'absolute', top: 4, left: 0, right: 0, bottom: 0,
          padding: '20px 36px',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-start', overflow: 'hidden',
        }}
      >
        {/* Identity zone */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 16,
            minHeight: 96,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <FeaturedCardAvatarCircle
            imageUrl={featuredCardImageUrl}
            circleSize={80}
            accentColor={accentColor}
            accentRgba={accentRgba}
            isOneDeckPlaceholder={isOneDeckPlaceholder}
            reducedMotion={reducedMotion}
          />
          <div style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            minWidth: 0, flex: 1,
            alignItems: isP1Layout ? 'flex-start' : 'flex-end',
            textAlign: isP1Layout ? 'left' : 'right',
          }}>
            {hasName ? (
              <div style={{ fontSize: 36, fontFamily: fonts.display, fontWeight: 700, color: glassText.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.0, marginBottom: 3, width: '100%' }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, marginBottom: 3 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}
            {archetype && (
              <div style={{ fontSize: 18, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                {archetype}
              </div>
            )}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>
        </div>

        {hasStats ? (
          <>
            {/* Color Distribution */}
            <div style={{ marginTop: 10, flexShrink: 0 }}>
              <MiniHeader text="COLOR" />
              <ColorPips dist={stats.colorDistribution} side={isP1Layout ? 'p1' : 'p2'} phase={phase} />
            </div>

            {/* Card Types */}
            {stats.typeDistribution.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text="CARD TYPES" />
                <DonutTypeChart
                  dist={stats.typeDistribution}
                  side={isP1Layout ? 'p1' : 'p2'}
                  phase={phase}
                  size={90}
                  legendPosition="side"
                  showTypePercentages={true}
                />
              </div>
            )}

            {/* Level Distribution */}
            {stats.costCurve.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text={levelHeader} />
                <MkDistributionBar
                  curve={stats.costCurve}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  reverse={!isP1Layout}
                />
              </div>
            )}

            {/* Cost Distribution */}
            {stats.secondaryCurve && stats.secondaryCurve.length > 0 && costLabelUnion.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text={costHeader} />
                <div style={{ marginTop: 26 }}>
                  <MiniBarChart
                    curve={stats.secondaryCurve}
                    labelUnion={costLabelUnion}
                    sharedMax={costSharedMax}
                    accentColor={accentColor}
                    phase={phase}
                    reverse={!isP1Layout}
                    compact={false}
                  />
                </div>
              </div>
            )}

            {/* Trait Breakdown — top 5 */}
            {top5Traits.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text={traitHeader} />
                <MiniHorizontalBars
                  entries={top5Traits}
                  localMax={traitMax}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={700}
                />
              </div>
            )}

            {/* Keywords — cap 12 */}
            {top12Keywords.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text="KEYWORDS" />
                <KeywordBubbleCloud
                  keywords={top12Keywords}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={740}
                  compact={false}
                />
                {keywordOverflow > 0 && (
                  <div style={{ fontSize: 12, fontWeight: 400, color: glassText.muted, marginTop: 4, textAlign: isP1Layout ? 'left' : 'right' }} aria-hidden="true">
                    +{keywordOverflow} more
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GundamRailPanel — condensed profile rail for Gundam Card Game (~25% col).
// Natural layout. Sections: identity → colors → card types → level →
//           cost → traits → zone composition (BinaryDistributionBar) →
//           AP|HP heatmaps (paired) → keywords (cap 12, compact).
// ---------------------------------------------------------------------------

export type GundamRailPanelProps = Omit<GundamDensePanelProps, 'fullWidth' | 'naturalLayout'>;

export function GundamRailPanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  featuredCardImageUrl,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelSharedMax: _ls,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelLabelUnion: _ll,
  costSharedMax,
  costLabelUnion,
  placeholderText,
}: GundamRailPanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const traitDist = stats?.subtypeDistributions?.[0] ?? null;
  const top5Traits = traitDist?.data
    ? [...traitDist.data].sort((a, b) => b.count - a.count).slice(0, 5)
    : [];
  const traitMax = top5Traits.length > 0 ? Math.max(...top5Traits.map(e => e.count)) : 1;
  const traitHeader = traitDist ? traitDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'TRAIT BREAKDOWN';

  const top12Keywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count).slice(0, 12)
    : [];
  const keywordOverflow = (stats?.keywordDistribution?.length ?? 0) - top12Keywords.length;

  const levelHeader = stats?.costCurveLabel ? stats.costCurveLabel.toUpperCase() : 'LEVEL DISTRIBUTION';
  const costHeader = stats?.secondaryCurveLabel ? stats.secondaryCurveLabel.toUpperCase() : 'COST DISTRIBUTION';

  // AP + HP heatmaps from additionalCurves — find by label regex (provisional labels)
  const apCurve = stats?.additionalCurves?.find(c => /\bap\b/i.test(c.label) || /attack/i.test(c.label));
  const hpCurve = stats?.additionalCurves?.find(c => /\bhp\b/i.test(c.label) || /health/i.test(c.label));
  const apSharedMax = apCurve ? Math.max(...apCurve.data.map(d => d.count), 1) : 1;
  const hpSharedMax = hpCurve ? Math.max(...hpCurve.data.map(d => d.count), 1) : 1;
  const apLabel = apCurve ? apCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'AP';
  const hpLabel = hpCurve ? hpCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'HP';

  // Binary distribution (Zone Composition — Earth/Space)
  const binaryEntries = stats?.binaryDistribution ?? null;
  const binaryFallback: [string, string] = stats?.binaryDistributionColors
    ? stats.binaryDistributionColors
    : [palette.coralGlow, palette.brilliantRose];
  const binaryHeader = stats?.binaryDistributionLabel ? stats.binaryDistributionLabel.toUpperCase() : 'ZONE COMPOSITION';

  return (
    <div
      aria-hidden="true"
      style={{ flex: 1, height: '100%', position: 'relative', background: glass.card, borderRadius: 16, border: `1px solid ${glassBorder.light}`, overflow: 'hidden' }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accentColor }} />
      <div style={{ position: 'absolute', top: 4, left: 0, right: 0, bottom: 0, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', overflow: 'hidden' }}>

        {/* Identity block */}
        <div className={animCls('dcao-names-enter')} style={{ display: 'flex', gap: 16, alignItems: 'center', minHeight: 80, flexShrink: 0 }}>
          <FeaturedCardAvatarCircle
            imageUrl={featuredCardImageUrl}
            circleSize={64}
            accentColor={accentColor}
            accentRgba={accentRgba}
            isOneDeckPlaceholder={isOneDeckPlaceholder}
            reducedMotion={reducedMotion}
          />
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, flex: 1, alignItems: 'flex-start' }}>
            {hasName ? (
              <div style={{ fontSize: 28, fontFamily: fonts.display, fontWeight: 700, color: glassText.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.0, marginBottom: 2, width: '100%' }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.muted, marginBottom: 2 }}>{isP1 ? 'Player 1' : 'Player 2'}</div>
            )}
            {archetype && <div style={{ fontSize: 15, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{archetype}</div>}
          </div>
        </div>

        {hasStats ? (
          <>
            <SectionDivider margin="12px 0" />
            <div style={{ flexShrink: 0 }}>
              <MiniHeader text="COLOR" />
              <ColorPips dist={stats.colorDistribution} side="p1" phase={phase} />
            </div>
            <SectionDivider margin="12px 0" />
            {stats.typeDistribution.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text="CARD TYPES" />
                  <DonutTypeChart dist={stats.typeDistribution} side="p1" phase={phase} size={90} legendPosition="side" showTypePercentages={true} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {stats.costCurve.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={levelHeader} />
                  <MkDistributionBar curve={stats.costCurve} side="p1" accentColor={accentColor} phase={phase} reverse={false} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {stats.secondaryCurve && stats.secondaryCurve.length > 0 && costLabelUnion.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={costHeader} />
                  <div style={{ marginTop: 26 }}>
                    <MiniBarChart curve={stats.secondaryCurve} labelUnion={costLabelUnion} sharedMax={costSharedMax} accentColor={accentColor} phase={phase} reverse={false} compact={false} />
                  </div>
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {top5Traits.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={traitHeader} />
                  <MiniHorizontalBars entries={top5Traits} localMax={traitMax} side="p1" accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={650} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {binaryEntries && binaryEntries.length >= 2 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={binaryHeader} />
                  <BinaryDistributionBar entries={binaryEntries} fallbackColors={binaryFallback} phase={phase} isEntering={isEntering} startDelay={680} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {(apCurve || hpCurve) && (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 16, flexShrink: 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {apCurve && apCurve.data.length > 0 ? (
                      <>
                        <MiniHeader text={apLabel} />
                        <MiniHeatmap data={apCurve.data} sharedMax={apSharedMax} accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={700} />
                      </>
                    ) : <div />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {hpCurve && hpCurve.data.length > 0 ? (
                      <>
                        <MiniHeader text={hpLabel} />
                        <MiniHeatmap data={hpCurve.data} sharedMax={hpSharedMax} accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={700} />
                      </>
                    ) : <div />}
                  </div>
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {top12Keywords.length > 0 && (
              <div style={{ flexShrink: 0 }}>
                <MiniHeader text="KEYWORDS" />
                <KeywordBubbleCloud keywords={top12Keywords} side="p1" accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={730} compact={true} />
                {keywordOverflow > 0 && (
                  <div style={{ fontSize: 12, fontWeight: 400, color: glassText.muted, marginTop: 4 }} aria-hidden="true">
                    +{keywordOverflow} more
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

